import React, { type FC } from "react";

import styled from "@emotion/styled";
import { useIntl } from "react-intl";

import {
  Flex,
  TEXT_VARIANT_BOLD_LIGHT,
  TEXT_VARIANT_ITALIC_LIGHT,
  TEXT_VARIANT_LIGHT,
  Text,
  TooltipUtils,
} from "mailup-web-components";

import { stringOrDefault } from "Helpers/strings";

import RenderWhen from "Components/RenderWhen";
import type {
  ChannelSubscriptionStatus,
  SearchResult,
} from "Pages/RecipientsList/model";
import IconHelpBadge from "../../../../icons/iconHelpBadge.svg";

const StyledHelpIcon = styled.img`
  width: 14px;
  height: 14px;
  cursor: pointer;
`;

const getStatusFilter = (status: ChannelSubscriptionStatus): string => {
  switch (status) {
    case "SUBSCRIBED":
      // #39BA7C
      return "brightness(0) saturate(100%) invert(62%) sepia(43%) saturate(600%) hue-rotate(108deg) brightness(90%)";
    case "PENDING":
      // #f8b309
      return "brightness(0) saturate(100%) invert(66%) sepia(100%) saturate(1200%)";
    case "UNSUBSCRIBED":
      // #ff6458
      return "brightness(0) saturate(100%) invert(55%) sepia(90%) saturate(1200%) hue-rotate(325deg) brightness(108%)";
    default:
      // #838383
      return "brightness(0) saturate(100%) invert(52%)";
  }
};

interface EmailSubscriptionDetailsProps {
  recipient: SearchResult;
  status: ChannelSubscriptionStatus;
}

const EmailSubscriptionDetails: FC<EmailSubscriptionDetailsProps> = ({
  recipient,
  status,
}) => {
  const intl = useIntl();

  //  {/* event='click' */}

  return (
    <>
      <TooltipUtils
        id="email-subscription-details-tooltip"
        type="light"
        effect="solid"
        className="c-tooltip"
        place="bottom"
      >
        <Flex flexDirection="column" gap="8px">
          {/* Iscrizione  */}
          {/** Richiesta Iscrizione  **/}
          <section>
            <Flex alignItems="center" gap="8px">
              <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
                {intl.formatMessage({
                  id: "Load.RecipientsList.SubscriptionConfirmationRequest",
                })}
              </Text>
              <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
                (
                {intl.formatMessage({
                  id: "Load.MailupCommon.Optional",
                })}
                ) :
              </Text>
            </Flex>

            <RenderWhen condition={recipient.EmailPendingDateExt !== undefined}>
              <Flex alignItems="center" gap="16px">
                <Text variant={TEXT_VARIANT_LIGHT}>
                  {recipient.EmailPendingDateExt}
                </Text>
                <Text variant={TEXT_VARIANT_LIGHT}>
                  IP:{" "}
                  {stringOrDefault(recipient.EmailSubscriptionRequestIP, "-")}
                </Text>
              </Flex>
            </RenderWhen>

            <RenderWhen condition={recipient.EmailPendingDateExt === undefined}>
              <Text variant={TEXT_VARIANT_LIGHT}>
                {intl.formatMessage({
                  id: "Load.RecipientsList.NoSubscriptionConfirmationRequest",
                })}
              </Text>
            </RenderWhen>
          </section>

          {/** Conferma iscrizione **/}
          <section>
            <Flex alignItems="center" gap="8px">
              <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
                {intl.formatMessage({
                  id: "Load.RecipientsList.SubscriptionConfirmation",
                })}
              </Text>
              <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
                (
                {intl.formatMessage({
                  id: "Load.MailupCommon.Optional",
                })}
                ) :
              </Text>
            </Flex>

            <RenderWhen
              condition={
                recipient.EmailConfirmationDateExt !== undefined &&
                recipient.ConfirmedOptin === true
              }
            >
              <Flex alignItems="center" gap="16px">
                <Text variant={TEXT_VARIANT_LIGHT}>
                  {recipient.EmailConfirmationDateExt}
                </Text>
                <Text variant={TEXT_VARIANT_LIGHT}>
                  IP: {stringOrDefault(recipient.EmailConfirmationIP, "-")}
                </Text>
              </Flex>
            </RenderWhen>

            <RenderWhen
              condition={
                !(
                  recipient.EmailConfirmationDateExt !== undefined &&
                  recipient.ConfirmedOptin === true
                )
              }
            >
              <Text variant={TEXT_VARIANT_LIGHT}>
                {intl.formatMessage({
                  id: "Load.RecipientsList.NoSubscriptionConfirmation",
                })}
              </Text>
            </RenderWhen>
          </section>

          {/** Iscrizione **/}
          <section>
            <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
              {intl.formatMessage({
                id: "Load.RecipientsList.Subscription",
              })}
            </Text>
            <Text variant={TEXT_VARIANT_LIGHT}>
              {stringOrDefault(recipient.EmailSubscriptionDateExt, "-")}
            </Text>
          </section>

          {/* Disiscrizione */}
          <RenderWhen
            condition={
              recipient.EmailSubscriptionStatus === "UNSUBSCRIBED" &&
              recipient.EmailUnsubscriptionDateExt !== undefined
            }
          >
            <section>
              <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
                {intl.formatMessage({
                  id: "Load.Common.Unsubscription",
                })}
              </Text>
              <Text variant={TEXT_VARIANT_LIGHT}>
                {stringOrDefault(recipient.EmailUnsubscriptionDateExt, "-")}
              </Text>
            </section>
          </RenderWhen>
        </Flex>
      </TooltipUtils>
      <StyledHelpIcon
        src={IconHelpBadge}
        alt=""
        style={{ filter: getStatusFilter(status) }}
        //data-event="click"
        data-tip
        data-for="email-subscription-details-tooltip"
      />
    </>
  );
};

export default EmailSubscriptionDetails;
